import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import AuthFlowContext from '@aurora/shared-client/components/context/AuthFlowContext/AuthFlowContext';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import useAuthFlow from '@aurora/shared-client/components/useAuthFlow';
import useGlobalState, { GlobalStateType } from '@aurora/shared-client/helpers/ui/GlobalState';
import useEndUserNextPage from '@aurora/shared-client/routes/useEndUserNextPage';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import { AuthFlow } from '@aurora/shared-client/types/enums';
import { AuthMechanism } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import Link from 'next/link';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import useTranslation from '../../useTranslation';
import useCommunityAccess from './useCommunityAccess';

export interface Props {
  /**
   * Class name(s) to apply to the link element.
   */
  className?: string;

  /**
   * Class name(s) to apply to the inner element.
   */
  textClassName?: string;

  /**
   * Type of authentication flow to use.
   */
  type?: AuthFlow;
}

/**
 * An authentication link that displays when the user is not logged in and
 * will open a [Authentication Modal](/docs/components-authentication-loginform--default) when selected.
 *
 * @author Adam Ayres, Dolan Halbrook, Willi Hyde
 */
const AuthenticationLink: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  textClassName,
  type = AuthFlow.LOGIN
}) => {
  const cx = useClassNameMapper();
  const tenant = useContext(TenantContext);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.AUTHENTICATION_LINK
  );
  const { router, loading: routesLoading } = useEndUserRoutes();
  const { nextUrl, nextRoute } = useEndUserNextPage(
    routesLoading ? null : router.getCurrentRouteAndParams()
  );
  const [, setMessageEditingState] = useGlobalState(GlobalStateType.MESSAGE_EDITING_STATE);
  const { canAccessLoginActions, canAccessRegistrationActions } = useCommunityAccess();
  const { showAuthenticationModal } = useContext(AuthFlowContext);
  const {
    getSsoAuthUrl,
    authProvidersData,
    ssoPropertiesData,
    loading: authFlowLoading
  } = useAuthFlow();
  const { ssoEnabled } = ssoPropertiesData?.community.ssoProperties || {};
  const {
    publicConfig: { multiAuthEnabled }
  } = tenant;

  const authProviders = authProvidersData?.authProviders.filter(
    authProvider => authProvider.enabled
  );
  const [localAuthProvider, ...rest] = authProvidersData?.authProviders ?? [];
  const authProviderInfo = {
    localAuthProvider: localAuthProvider ?? null,
    availableAuthProvider: [...rest],
    enabledAuthProvider: authProviders ?? []
  };

  if (
    ((type === AuthFlow.LOGIN || type === AuthFlow.MULTI_AUTH_LOGIN) && !canAccessLoginActions) ||
    (type === AuthFlow.REGISTRATION && !canAccessRegistrationActions) ||
    textLoading ||
    authFlowLoading
  ) {
    return null;
  }
  async function onSignIn(): Promise<void> {
    showAuthenticationModal(type, nextRoute, authProviderInfo);
    setMessageEditingState(null);
  }

  const isMultiAuthLink =
    multiAuthEnabled &&
    authProviders.length === 1 &&
    authProviders[0].authMechanism !== AuthMechanism.Local;

  const isSsoLink = !multiAuthEnabled && checkPolicy(ssoEnabled);

  const renderLink = (
    <Link
      data-testid={`AuthenticationLink.${type}`}
      className={cx(className)}
      href={getSsoAuthUrl(nextUrl, type === AuthFlow.REGISTRATION)}
      prefetch={false}
    >
      {formatMessage(`title.${type}`)}
    </Link>
  );

  const renderButton = (
    <Button
      variant={ButtonVariant.LINK}
      onClick={onSignIn}
      data-testid={`AuthenticationLink.${type}`}
      className={cx(className)}
    >
      <span className={cx(textClassName)}>{formatMessage(`title.${type}`)}</span>
    </Button>
  );

  return isMultiAuthLink || isSsoLink ? renderLink : renderButton;
};

export default AuthenticationLink;
